import React, { useState, useEffect } from 'react';
import { createAppointment, updateAppointment } from '../api';

const DIAS_RECURRENCIA = [
  { num: 1, label: 'Lun' }, { num: 2, label: 'Mar' }, { num: 3, label: 'Mié' },
  { num: 4, label: 'Jue' }, { num: 5, label: 'Vie' }, { num: 6, label: 'Sáb' }, { num: 0, label: 'Dom' },
];

function generarFechasRecurrentes(fechaInicio, fechaFin, diasSemana) {
  const fechas = [];
  const fin = new Date(fechaFin + 'T12:00:00');
  const actual = new Date(fechaInicio + 'T12:00:00');
  while (actual <= fin) {
    if (diasSemana.includes(actual.getDay())) {
      fechas.push(actual.toISOString().split('T')[0]);
    }
    actual.setDate(actual.getDate() + 1);
  }
  return fechas;
}

const formatearFechaCorta = (iso) => iso.split('-').reverse().join('/');

// Formulario de alta de turno/evento/recurrente (3 tabs), compartido entre el
// panel del día de Calendario.jsx y el modal de "Agregar turno/evento" de
// AccionesRapidas (PanelInicio). El destinatario decide si `fecha` es editable
// pasando `onFechaChange` (AccionesRapidas) o no (Calendario, donde la fecha
// se fija haciendo clic en una celda del calendario).
function FormularioTurno({ pacientes, fecha, onFechaChange, onCreado, prellenado }) {
  const [creando, setCreando] = useState(false);
  const [errorCita, setErrorCita] = useState('');
  const [tipoForm, setTipoForm] = useState('appointment');

  // Form: turno individual
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState('');
  const [horaCita, setHoraCita] = useState('');
  const [duracionCita, setDuracionCita] = useState(60);
  const [notasCita, setNotasCita] = useState('');

  // Precarga hora/duración cuando el caller selecciona un rango (ej. arrastre
  // en la grilla de Calendario.jsx). `prellenado` debe cambiar de referencia
  // en cada selección (ver `ts`) para que el efecto dispare aunque los
  // valores sean iguales a la selección anterior.
  useEffect(() => {
    if (!prellenado) return;
    setTipoForm('appointment');
    if (prellenado.hora) setHoraCita(prellenado.hora);
    setDuracionCita(prellenado.duracion);
  }, [prellenado]);

  // Form: evento general
  const [tituloEvento, setTituloEvento] = useState('');
  const [horaEvento, setHoraEvento] = useState('');
  const [notasEvento, setNotasEvento] = useState('');

  // Form: turno recurrente
  const [recurPaciente, setRecurPaciente] = useState('');
  const [recurHora, setRecurHora] = useState('');
  const [recurDuracion, setRecurDuracion] = useState(60);
  const [recurInicio, setRecurInicio] = useState('');
  const [recurFin, setRecurFin] = useState('');
  const [recurDias, setRecurDias] = useState([]);
  const [recurProgreso, setRecurProgreso] = useState('');

  const pacientesActivos = pacientes.filter(p => p.is_active !== false);

  const agregarCita = async () => {
    if (!pacienteSeleccionado || !fecha) return;
    setCreando(true); setErrorCita('');
    try {
      const nueva = await createAppointment(
        Number(pacienteSeleccionado), fecha, horaCita || null, notasCita || null
      );
      const final = (horaCita && duracionCita && Number(duracionCita) !== 60)
        ? await updateAppointment(nueva.id, { duration: Number(duracionCita) })
        : nueva;
      onCreado(final);
      setPacienteSeleccionado(''); setHoraCita(''); setDuracionCita(60); setNotasCita('');
    } catch (err) { setErrorCita(err.message); }
    finally { setCreando(false); }
  };

  const agregarEvento = async () => {
    if (!tituloEvento.trim() || !fecha) return;
    setCreando(true); setErrorCita('');
    try {
      const nuevo = await createAppointment(null, fecha, horaEvento || null, notasEvento || null, tituloEvento.trim());
      onCreado(nuevo);
      setTituloEvento(''); setHoraEvento(''); setNotasEvento('');
    } catch (err) { setErrorCita(err.message); }
    finally { setCreando(false); }
  };

  const toggleDiaRecur = (num) => setRecurDias(prev => prev.includes(num) ? prev.filter(d => d !== num) : [...prev, num]);
  const fechasRecurrentes = recurInicio && recurFin && recurDias.length > 0
    ? generarFechasRecurrentes(recurInicio, recurFin, recurDias) : [];

  const agregarRecurrentes = async () => {
    if (!recurPaciente || fechasRecurrentes.length === 0) return;
    setCreando(true); setErrorCita(''); setRecurProgreso('');
    let creados = 0; const errores = [];
    for (const fechaTurno of fechasRecurrentes) {
      try {
        const nueva = await createAppointment(Number(recurPaciente), fechaTurno, recurHora || null);
        const final = (recurHora && recurDuracion && Number(recurDuracion) !== 60)
          ? await updateAppointment(nueva.id, { duration: Number(recurDuracion) })
          : nueva;
        onCreado(final);
        creados++;
        setRecurProgreso(`${creados}/${fechasRecurrentes.length} turnos creados...`);
      } catch { errores.push(fechaTurno); }
    }
    if (errores.length > 0) {
      const maxMostrar = 8;
      const fechasMostradas = errores.slice(0, maxMostrar).map(formatearFechaCorta).join(', ');
      const resto = errores.length - maxMostrar;
      const detalle = resto > 0 ? `${fechasMostradas} y ${resto} más` : fechasMostradas;
      setErrorCita(`${creados} turnos creados. No se crearon ${errores.length} por solapamiento: ${detalle}.`);
    } else {
      setRecurProgreso(`✓ ${creados} turnos creados correctamente.`);
    }
    setRecurPaciente(''); setRecurHora(''); setRecurDuracion(60); setRecurInicio(''); setRecurFin(''); setRecurDias([]);
    setCreando(false);
  };

  return (
    <div className="cal-form-turno">
      {onFechaChange && tipoForm !== 'recurring' && (
        <label className="cal-recur-label" style={{ marginBottom: 8 }}>
          Fecha
          <div className="recom-card-fecha-row">
            <input type="date" className="ficha-input date-input" value={fecha} onChange={e => onFechaChange(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
            <button type="button" className="recom-btn-hoy" onClick={() => onFechaChange(new Date().toISOString().slice(0, 10))}>
              Hoy
            </button>
          </div>
        </label>
      )}

      <div className="cal-form-tabs">
        <button className={`cal-form-tab ${tipoForm === 'appointment' ? 'cal-form-tab-activo' : ''}`} onClick={() => { setTipoForm('appointment'); setErrorCita(''); }}>Turno</button>
        <button className={`cal-form-tab ${tipoForm === 'event' ? 'cal-form-tab-activo' : ''}`} onClick={() => { setTipoForm('event'); setErrorCita(''); }}>Evento general</button>
        <button className={`cal-form-tab ${tipoForm === 'recurring' ? 'cal-form-tab-activo' : ''}`} onClick={() => { setTipoForm('recurring'); setErrorCita(''); }}>Recurrente</button>
      </div>

      {errorCita && <div className="cal-error">{errorCita}</div>}

      {tipoForm === 'appointment' && (
        <div className="cal-agregar-cita">
          <select className="ficha-input cal-select" value={pacienteSeleccionado} onChange={e => setPacienteSeleccionado(e.target.value)}>
            <option value="">Seleccionar paciente...</option>
            {pacientesActivos.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="time" className="ficha-input cal-hora" value={horaCita} onChange={e => setHoraCita(e.target.value)} style={{ flex: 1 }} />
            <input type="number" className="ficha-input" value={duracionCita} onChange={e => setDuracionCita(e.target.value)}
              min={15} max={480} placeholder="min" title="Duración en minutos" style={{ flex: '0 0 72px' }} />
          </div>
          <input type="text" className="ficha-input cal-notas" placeholder="Notas (opcional)" value={notasCita} onChange={e => setNotasCita(e.target.value)} />
          <button className="btn-guardar-sesion" onClick={agregarCita} disabled={!pacienteSeleccionado || creando}>
            {creando ? 'Guardando...' : 'Agregar turno'}
          </button>
        </div>
      )}

      {tipoForm === 'event' && (
        <div className="cal-agregar-cita">
          <input type="text" className="ficha-input cal-select" placeholder="Título del evento *" value={tituloEvento} onChange={e => setTituloEvento(e.target.value)} />
          <input type="time" className="ficha-input cal-hora" value={horaEvento} onChange={e => setHoraEvento(e.target.value)} />
          <input type="text" className="ficha-input cal-notas" placeholder="Notas (opcional)" value={notasEvento} onChange={e => setNotasEvento(e.target.value)} />
          <button className="btn-guardar-sesion" onClick={agregarEvento} disabled={!tituloEvento.trim() || creando}>
            {creando ? 'Guardando...' : 'Agregar evento'}
          </button>
        </div>
      )}

      {tipoForm === 'recurring' && (
        <div className="cal-form-recurrente">
          <div className="cal-agregar-cita">
            <select className="ficha-input cal-select" value={recurPaciente} onChange={e => setRecurPaciente(e.target.value)}>
              <option value="">Seleccionar paciente...</option>
              {pacientesActivos.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="time" className="ficha-input cal-hora" value={recurHora} onChange={e => setRecurHora(e.target.value)} style={{ flex: 1 }} />
              <input type="number" className="ficha-input" value={recurDuracion} onChange={e => setRecurDuracion(e.target.value)}
                min={15} max={480} placeholder="min" title="Duración en minutos" style={{ flex: '0 0 72px' }} />
            </div>
          </div>
          <div className="cal-agregar-cita">
            <label className="cal-recur-label">
              Desde
              <div className="recom-card-fecha-row">
                <input type="date" className="ficha-input date-input" value={recurInicio} onChange={e => setRecurInicio(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
                <button type="button" className="recom-btn-hoy" onClick={() => setRecurInicio(new Date().toISOString().slice(0, 10))}>
                  Hoy
                </button>
              </div>
            </label>
            <label className="cal-recur-label">
              Hasta
              <input type="date" className="ficha-input date-input" value={recurFin} onChange={e => setRecurFin(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
            </label>
          </div>
          <div className="cal-dias-semana">
            <span className="cal-recur-subtitulo">Días de la semana:</span>
            <div className="cal-dias-grupo">
              {DIAS_RECURRENCIA.map(({ num, label }) => (
                <button key={num} type="button"
                  className={`cal-dia-btn ${recurDias.includes(num) ? 'cal-dia-btn-activo' : ''}`}
                  onClick={() => toggleDiaRecur(num)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {fechasRecurrentes.length > 0 && (
            <p className="cal-recur-resumen">
              Se crearán <strong>{fechasRecurrentes.length}</strong> turnos entre el{' '}
              {fechasRecurrentes[0].split('-').reverse().join('/')} y el{' '}
              {fechasRecurrentes[fechasRecurrentes.length - 1].split('-').reverse().join('/')}.
            </p>
          )}
          {recurProgreso && <p className="cal-recur-progreso">{recurProgreso}</p>}
          <button className="btn-guardar-sesion" onClick={agregarRecurrentes}
            disabled={!recurPaciente || fechasRecurrentes.length === 0 || creando}>
            {creando ? recurProgreso || 'Creando turnos...' : `Crear ${fechasRecurrentes.length || ''} turnos`}
          </button>
        </div>
      )}
    </div>
  );
}

export default FormularioTurno;
